/**
 * GHLLaunchPage — landing route for `/ghl/launch?owner=...&locationid=...`.
 *
 * Flow:
 *   1. Parse query params from URL (set by GHL after substituting
 *      {{location.id}}, {{user.name}}, {{user.email}}).
 *   2. If user isn't logged in, bounce to /login?next=<current url so they
 *      come back to /ghl/launch with the same query string after auth.
 *   3. Call backend GET /api/ghl/launch to resolve the matching Voxinity
 *      sub-account.
 *   4. On success: navigate to `/workspace/<owner>/sub-account/<id>`.
 *   5. On 404: show a friendly "this GHL sub-account isn't connected"
 *      panel with a link back to the agency's Sub-Accounts page so the
 *      agency owner can connect it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Link2Off, ArrowRight, ExternalLink, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATE = {
  IFRAME_LAUNCHER: 'iframe_launcher',  // Inside GHL iframe — show "Open in Voxinity" pop-out
  RESOLVING: 'resolving',
  NOT_CONNECTED: 'not_connected',
  NO_ACCESS: 'no_access',
  BAD_REQUEST: 'bad_request',
  ERROR: 'error',
};

// True when the page is rendered inside any iframe. We rely on this to
// decide between "resolve immediately" (top window) and "show pop-out
// launcher" (iframe). Inside an iframe both Google OAuth and the OTP
// login flow are crippled by browser policies (Google's 403, partitioned
// localStorage, third-party cookie blocks) so we don't even try — we
// just give the user a single button that opens the same /ghl/launch
// URL in a top-level tab where auth works normally.
const isInIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

const GHLLaunchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, loading: authLoading } = useAuth();
  const insideIframe = useMemo(() => isInIframe(), []);
  // Start in RESOLVING regardless of iframe context — the backend session
  // cookie (set at login with SameSite=None) follows the user into the
  // iframe, so an already-logged-in user gets auto-resolved with zero
  // clicks. Only fall back to the iframe-launcher CTA on 401.
  const [state, setState] = useState(STATE.RESOLVING);
  const [owner, setOwner] = useState(null);

  const params = new URLSearchParams(location.search);
  const ownerParam = params.get('owner') || '';
  const locationid = params.get('locationid') || '';

  // Build the absolute top-level URL we want the user to open in a new
  // tab. We include all original query params so the destination tab
  // resolves to the same sub-account.
  const topLevelUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.origin + location.pathname + location.search;
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Wait for AuthContext to finish bootstrapping. The bootstrap call
    // also sets the session cookie on the response (via /api/me), which
    // we want completed before our resolve attempt — that way the first
    // resolve has every available credential.
    if (authLoading) return;

    if (!ownerParam || !locationid || locationid.includes('{{')) {
      setState(STATE.BAD_REQUEST);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/ghl/launch', {
          params: { owner: ownerParam, locationid },
        });
        if (cancelled) return;
        const redirect = res?.data?.redirect_url;
        if (redirect) {
          navigate(redirect, { replace: true });
        } else {
          setState(STATE.NOT_CONNECTED);
          setOwner(ownerParam);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401) {
          // Not authed. Inside an iframe we can't run the login flow
          // (Google blocks OAuth in iframes; OTP cookies/storage are
          // partitioned). Show the pop-out launcher card so the user
          // signs in once in a new tab — the resulting session cookie
          // follows them back into every future iframe load.
          if (insideIframe) {
            setState(STATE.IFRAME_LAUNCHER);
          } else {
            const next = encodeURIComponent(location.pathname + location.search);
            navigate(`/login?next=${next}`, { replace: true });
          }
        } else if (status === 404) {
          setState(STATE.NOT_CONNECTED);
          setOwner(ownerParam);
        } else if (status === 403) {
          setState(STATE.NO_ACCESS);
        } else if (status === 400) {
          setState(STATE.BAD_REQUEST);
        } else {
          setState(STATE.ERROR);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [api, authLoading, ownerParam, locationid, navigate, location.pathname, location.search, insideIframe]);

  return (
    <div className="min-h-screen bg-space-950 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-space-900/80 p-8 shadow-2xl">
        {state === STATE.IFRAME_LAUNCHER && (
          <div className="text-center" data-testid="ghl-launch-iframe-launcher">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center mb-5 shadow-[0_8px_24px_-4px_rgba(45,212,191,0.5)]">
              <Shield size={26} className="text-space-900" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-bold mb-2">Log in to authorize access</h2>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              Sign in to Voxinity to view this sub-account.
            </p>
            <a
              href={topLevelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-400 text-space-900 font-semibold hover:bg-teal-500 transition-all shadow-lg shadow-teal-400/30"
              data-testid="ghl-launch-popout-btn"
            >
              Log in to authorize <ExternalLink size={16} />
            </a>
            <p className="text-[11px] text-gray-500 mt-5 leading-relaxed">
              You only need to do this once per browser. Future clicks from inside GHL will skip
              this step automatically.
            </p>
          </div>
        )}

        {state === STATE.RESOLVING && (
          <div className="flex flex-col items-center text-center gap-4" data-testid="ghl-launch-resolving">
            <Loader2 size={32} className="text-teal-300 animate-spin" />
            <h2 className="text-lg font-semibold">Connecting you to Voxinity…</h2>
            <p className="text-sm text-gray-400">
              Looking up the right sub-account for this GoHighLevel location.
            </p>
          </div>
        )}

        {state === STATE.NOT_CONNECTED && (
          <div className="text-center" data-testid="ghl-launch-not-connected">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center mb-4">
              <Link2Off size={26} className="text-amber-300" />
            </div>
            <h2 className="text-lg font-semibold mb-2">This GHL location isn&apos;t connected to Voxinity yet</h2>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              No Voxinity sub-account is linked to this GoHighLevel location inside your agency.
              Connect this location from the Sub-Accounts page to start using AI agents here.
            </p>
            {owner && (
              <button
                onClick={() => navigate(`/workspace/${owner}/sub-accounts`)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-400 text-space-900 font-semibold hover:bg-teal-500"
                data-testid="ghl-launch-go-to-sub-accounts-btn"
              >
                Go to Sub-Accounts <ArrowRight size={16} />
              </button>
            )}
          </div>
        )}

        {state === STATE.NO_ACCESS && (
          <div className="text-center" data-testid="ghl-launch-no-access">
            <h2 className="text-lg font-semibold mb-2">You don&apos;t have access to this Voxinity agency</h2>
            <p className="text-sm text-gray-400 mb-6">
              Ask your agency owner to invite you as a workspace member so this menu link can resolve.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white font-semibold hover:bg-white/15"
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {state === STATE.BAD_REQUEST && (
          <div className="text-center" data-testid="ghl-launch-bad-request">
            <h2 className="text-lg font-semibold mb-2">This menu link isn&apos;t configured correctly</h2>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              The GoHighLevel <code className="text-teal-300">{`{{location.id}}`}</code> placeholder didn&apos;t resolve.
              This usually means the Custom Menu Link is configured to show on the
              <strong> agency level</strong> instead of sub-accounts. Open GHL → Settings →
              Custom Menu Links and toggle the link to <strong>show on sub-accounts only</strong>.
            </p>
          </div>
        )}

        {state === STATE.ERROR && (
          <div className="text-center" data-testid="ghl-launch-error">
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-400 mb-6">
              We couldn&apos;t resolve this link right now. Please try again — if it keeps
              happening, contact support.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white font-semibold hover:bg-white/15"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GHLLaunchPage;
